import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertLevel, PriceEditType } from '@/types';
import { createPriceValidationBannerMessage } from '@/domain/pricing/utils/validation';
import { createStyles } from '@/contexts/theme-context';
import {
  formatGrossProfitDisplay,
  formatPriceDisplayFromMicros,
} from '@/utils/price-editing';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { PriceOverrideInfo } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PriceOverrideInfoDisplay } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/price-override-info';
import { stripCommas } from '@/domain/pricing/utils/pricing-utils';
import { TextInput } from '@/experiences/sales-portal/components/text-input';
import { Typography, XStack, YStack } from '@usepepper/sprout-kit-ui';
import { useDebouncedCallback } from 'use-debounce';
import { usePriceEditorState } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-editor-state';
import { usePriceEditUnitDisplay } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-price-edit-unit-display';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';
import { ValidationBanner } from '@/experiences/sales-portal/components/validation-banner';

const PRICE_INPUT_REGEX = /^[\d,]*\.?\d*$/;
const GROSS_PROFIT_INPUT_REGEX = /^-?\d*\.?\d*$/;
const VALIDATION_DEBOUNCE_MS = 300;

interface OrderEntryMobilePriceEditFieldsProps {
  canSetManualOverrideExpiration: boolean;
  isGrossProfitHidden: boolean;
  onInputFocusChange?: (isFocused: boolean) => void;
  onSave: (editor: PriceEditor, variantPackUUID: string) => Promise<boolean>;
  priceDisplayString: string;
  priceOverrideInfo?: PriceOverrideInfo;
  unitCount: number;
  unitPriceInfo?: string;
  unitSuffix?: string;
  variantPackUUID: string;
}

export const OrderEntryMobilePriceEditFields: React.FC<
  OrderEntryMobilePriceEditFieldsProps
> = ({
  canSetManualOverrideExpiration,
  isGrossProfitHidden,
  onInputFocusChange,
  onSave,
  priceDisplayString,
  priceOverrideInfo,
  unitCount,
  unitPriceInfo,
  unitSuffix,
  variantPackUUID,
}) => {
  const [styles, theme] = useStyles();
  const editor = usePricingStore.getState().editors.get(variantPackUUID);
  const editorState = usePriceEditorState(variantPackUUID);
  const {
    isPerUnit,
    secondaryPriceDisplay,
    showSecondaryPrice,
    unitBadgeLabel,
  } = usePriceEditUnitDisplay({
    editor,
    editorState,
    priceDisplayString,
    unitCount,
    unitPriceInfo,
    unitSuffix,
  });
  const priceLabel = i18n.t(
    'sales_portal.order_entry.item_detail.pricing.price',
  );
  const grossProfitLabel = i18n.t(
    'sales_portal.order_entry.item_detail.pricing.gp_percent',
  );
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);

  const debouncedValidate = useDebouncedCallback(() => {
    if (!editor) {
      return;
    }
    const result = editor.validate(editor.getState());
    setValidationMessage(
      !result.valid && result.reason
        ? createPriceValidationBannerMessage(result.reason, result.bound)
        : null,
    );
  }, VALIDATION_DEBOUNCE_MS);

  const handlePriceChange = useCallback(
    (text: string) => {
      if (text && !PRICE_INPUT_REGEX.test(text)) {
        return;
      }
      setIsEditing(true);
      editor?.updatePrice(stripCommas(text));
      debouncedValidate();
    },
    [debouncedValidate, editor],
  );

  const handleGrossProfitChange = useCallback(
    (text: string) => {
      if (text && !GROSS_PROFIT_INPUT_REGEX.test(text)) {
        return;
      }
      if (parseFloat(text) >= 100) {
        return;
      }
      setIsEditing(true);
      editor?.updateGrossProfit(text);
      debouncedValidate();
    },
    [debouncedValidate, editor],
  );

  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(blurTimeoutRef.current), []);

  const handleFocus = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
    onInputFocusChange?.(true);
  }, [onInputFocusChange]);

  const handleBlur = useCallback(async () => {
    setIsEditing(false);
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => onInputFocusChange?.(false), 0);
    if (!editor) {
      return;
    }
    const currentState = editor.getState();
    if (!currentState.isDirty) {
      return;
    }

    const validation = editor.validate(currentState);
    if (!validation.valid) {
      editor.clearEdits();
      setValidationMessage(null);
      return;
    }

    setValidationMessage(null);
    await onSave(editor, variantPackUUID);
  }, [editor, onInputFocusChange, onSave, variantPackUUID]);

  if (!editor || !editorState || !editor.isPriceEditingEnabled(true)) {
    return null;
  }

  const isSaving = editor.isSaveInProgress();
  const isUserEditing = isEditing || editorState.isDirty;
  const priceFallbackText = formatPriceDisplayFromMicros(
    editor.getCurrentPriceInMicros() ?? editor.getBasePriceInMicros() ?? null,
  );
  const priceDisplayValue = isUserEditing
    ? (editorState.priceDisplayText ?? '')
    : editorState.priceDisplayText || priceFallbackText;
  const grossProfitFallbackText = formatGrossProfitDisplay(
    editor.getGrossProfitPercent(),
  );
  const grossProfitDisplayValue = isUserEditing
    ? (editorState.grossProfitDisplayText ?? '')
    : editorState.grossProfitDisplayText || grossProfitFallbackText;
  const hasOverride = priceOverrideInfo?.activeOverride !== undefined;
  const showTemporaryIndicator =
    canSetManualOverrideExpiration && !!priceOverrideInfo?.isTemporaryOverride;
  const activeValidationMessage = editorState.isDirty
    ? validationMessage
    : null;

  return (
    <YStack gap="$8">
      <YStack gap="$4">
        <XStack gap="$12">
          <YStack flex={1} minWidth={0}>
            <Typography size="$sm">{priceLabel}</Typography>
          </YStack>
          {isGrossProfitHidden ? null : (
            <YStack flex={1} minWidth={0}>
              <Typography size="$sm">{grossProfitLabel}</Typography>
            </YStack>
          )}
        </XStack>
        <XStack alignItems="center" gap="$12">
          <XStack alignItems="center" flex={1} minWidth={0}>
            <TextInput
              accessibilityLabel={
                unitBadgeLabel
                  ? i18n.t(
                      'sales_portal.order_entry.item_detail.pricing.price_per_unit',
                      { unit: unitBadgeLabel },
                    )
                  : priceLabel
              }
              containerStyle={styles.priceInputContainer}
              editable={!isSaving}
              keyboardType="decimal-pad"
              onBlur={handleBlur}
              onChangeText={handlePriceChange}
              onFocus={handleFocus}
              value={priceDisplayValue}
            />
            {unitBadgeLabel ? (
              <XStack
                accessibilityElementsHidden
                alignItems="center"
                borderColor="$borderPrimary"
                borderRadius="$4"
                borderWidth={1}
                importantForAccessibility="no-hide-descendants"
                marginLeft="$4"
                paddingHorizontal="$4"
                paddingVertical="$2"
              >
                <Typography
                  color={theme.sk.color.text.tertiary}
                  numberOfLines={1}
                  size="$xs"
                >
                  {unitBadgeLabel}
                </Typography>
              </XStack>
            ) : null}
          </XStack>
          {isGrossProfitHidden ? null : (
            <YStack flex={1} minWidth={0}>
              <TextInput
                accessibilityLabel={grossProfitLabel}
                editable={!isSaving}
                keyboardType="numbers-and-punctuation"
                onBlur={handleBlur}
                onChangeText={handleGrossProfitChange}
                onFocus={handleFocus}
                value={grossProfitDisplayValue}
              />
            </YStack>
          )}
        </XStack>
        {secondaryPriceDisplay ? (
          <Typography
            color={theme.sk.color.text.tertiary}
            numberOfLines={1}
            size="$sm"
          >
            {secondaryPriceDisplay}
          </Typography>
        ) : null}
        {hasOverride && priceOverrideInfo ? (
          <XStack gap="$12">
            <YStack flex={1} minWidth={0}>
              <PriceOverrideInfoDisplay
                defaultSecondaryValue={
                  showSecondaryPrice
                    ? isPerUnit
                      ? priceOverrideInfo.defaultPackPriceDisplay
                      : priceOverrideInfo.defaultUnitPriceDisplay
                    : undefined
                }
                defaultValue={
                  isPerUnit
                    ? (priceOverrideInfo.defaultUnitPriceDisplay ?? '')
                    : priceOverrideInfo.defaultPackPriceDisplay
                }
                editedByName={priceOverrideInfo.editedByName}
                expiryDateDisplay={priceOverrideInfo.expiryDateDisplay}
                isInline
                lastValueLabelKey="sales_portal.order_entry.table.temporary_adjustment.last_price"
                showOverrideIndicator={
                  priceOverrideInfo.activeOverride === PriceEditType.Price
                }
                showTemporaryIndicator={showTemporaryIndicator}
              />
            </YStack>
            {isGrossProfitHidden ? null : (
              <YStack flex={1} minWidth={0}>
                <PriceOverrideInfoDisplay
                  defaultValue={priceOverrideInfo.defaultGrossProfitDisplay}
                  editedByName={priceOverrideInfo.editedByName}
                  expiryDateDisplay={priceOverrideInfo.expiryDateDisplay}
                  isInline
                  lastValueLabelKey="sales_portal.order_entry.table.temporary_adjustment.last_gross_profit"
                  showOverrideIndicator={
                    priceOverrideInfo.activeOverride ===
                    PriceEditType.GrossProfit
                  }
                  showTemporaryIndicator={showTemporaryIndicator}
                />
              </YStack>
            )}
          </XStack>
        ) : null}
      </YStack>
      {activeValidationMessage ? (
        <ValidationBanner
          level={AlertLevel.Warning}
          message={activeValidationMessage}
        />
      ) : null}
    </YStack>
  );
};

const useStyles = createStyles(() => ({
  priceInputContainer: {
    flex: 1,
  },
}));
